import type { SimulatorCategoryApplyFilter } from './SimulatorFilterPanel.types';

/** Mirrors global-filter `excludeIncludeEngine` for simulator category apply payloads. */
export function updateSimulatorCategoryApplyFilter(
    current: SimulatorCategoryApplyFilter | undefined,
    optionId: string,
    checked: boolean,
    usedSelectAll: boolean,
): SimulatorCategoryApplyFilter | undefined {
    if (usedSelectAll) {
        if (!checked) {
            const exclusion = [...(current?.exclusion ?? [])];
            if (!exclusion.includes(optionId)) {
                exclusion.push(optionId);
            }
            return { exclusion };
        }

        const exclusion = (current?.exclusion ?? []).filter((id) => id !== optionId);
        if (exclusion.length === 0) {
            // Select All with no unticks — same as global filter `{}`.
            return { inclusion: [] };
        }
        return { exclusion };
    }

    if (checked) {
        const inclusion = [...(current?.inclusion ?? [])];
        if (!inclusion.includes(optionId)) {
            inclusion.push(optionId);
        }
        return { inclusion };
    }

    const inclusion = (current?.inclusion ?? []).filter((id) => id !== optionId);
    return inclusion.length > 0 ? { inclusion } : undefined;
}

/** Empty apply filter for a category — Select All with no exclusions (global filter `{}`). */
export function emptySimulatorCategoryApplyFilter(): SimulatorCategoryApplyFilter {
    return { inclusion: [] };
}

export function isSimulatorCategoryApplyFilterActive(
    applyFilter: SimulatorCategoryApplyFilter | undefined,
): boolean {
    if (!applyFilter) {
        return false;
    }
    if (applyFilter.inclusion !== undefined) {
        return true;
    }
    return (applyFilter.exclusion?.length ?? 0) > 0;
}

type CategoryApplyFilterTrustOptions = {
    isSelectAll?: boolean;
    usedSelectAll?: boolean;
};

/** True when persisted/draft apply metadata matches the current category selection shape. */
export function isTrustedPrecomputedCategoryApplyFilter(
    applyFilter: SimulatorCategoryApplyFilter | undefined,
    selectedIds: string[],
    { isSelectAll = false, usedSelectAll = false }: CategoryApplyFilterTrustOptions = {},
): boolean {
    if (!applyFilter) {
        return false;
    }

    if ((applyFilter.exclusion?.length ?? 0) > 0) {
        return usedSelectAll;
    }

    if (applyFilter.inclusion !== undefined) {
        if (applyFilter.inclusion.length === 0) {
            return (isSelectAll || usedSelectAll) && selectedIds.length === 0;
        }

        if (usedSelectAll || isSelectAll) {
            return false;
        }

        if (applyFilter.inclusion.length !== selectedIds.length) {
            return false;
        }

        const selectedSet = new Set(selectedIds);
        return applyFilter.inclusion.every((id) => selectedSet.has(id));
    }

    return false;
}

/** Builds authoritative apply metadata from panel draft state at Apply time. */
export function buildCategoryApplyFiltersFromPanelState(
    categorySelections: Record<string, string[]>,
    categorySelectAll: Record<string, boolean>,
    categoryUsedSelectAll: Record<string, boolean>,
    categoryDraftApplyFilters: Record<string, SimulatorCategoryApplyFilter>,
): Record<string, SimulatorCategoryApplyFilter> | undefined {
    const result: Record<string, SimulatorCategoryApplyFilter> = {};
    const categoryKeys = new Set([
        ...Object.keys(categorySelections),
        ...Object.keys(categorySelectAll),
    ]);

    categoryKeys.forEach((categoryKey) => {
        const selectedIds = categorySelections[categoryKey] ?? [];
        const isSelectAll = Boolean(categorySelectAll[categoryKey]);
        const usedSelectAll = Boolean(categoryUsedSelectAll[categoryKey]);
        const draft = categoryDraftApplyFilters[categoryKey];

        if (isSelectAll && selectedIds.length === 0) {
            result[categoryKey] = draft ?? emptySimulatorCategoryApplyFilter();
            return;
        }

        if (usedSelectAll) {
            if (
                isTrustedPrecomputedCategoryApplyFilter(draft, selectedIds, {
                    isSelectAll,
                    usedSelectAll,
                })
            ) {
                result[categoryKey] = draft!;
            }
            return;
        }

        if (selectedIds.length > 0) {
            result[categoryKey] = { inclusion: [...selectedIds] };
        }
    });

    return Object.keys(result).length > 0 ? result : undefined;
}
